import { useRef, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { Button, Dialog, ScrollArea, Text } from '~/shared/ui'
import { MAX_FILES } from '../../model/constants'
import { selectIsUploadEnabled, useUploadQueueStore } from '../../model/use-upload-queue-store'
import { UploadModalFileRow } from './upload-modal-file-row'

export function UploadModal(): React.ReactElement | null {
  const isOpen = useUploadQueueStore((s) => s.isModalOpen)
  const stagedFiles = useUploadQueueStore((s) => s.modalStagedFiles)
  const isUploadEnabled = useUploadQueueStore(selectIsUploadEnabled)
  const supportedCount = stagedFiles.filter((s) => s.isSupported).length

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [overflowMessage, setOverflowMessage] = useState<string | null>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)

  const handleClose = (): void => {
    setOverflowMessage(null)
    setIsDraggingOver(false)
    useUploadQueueStore.getState().closeModal()
  }

  const handleAddFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return
    const { rejectedOverflow } = useUploadQueueStore.getState().addFilesToModal(Array.from(files))
    setOverflowMessage(
      rejectedOverflow > 0
        ? `You can upload up to ${MAX_FILES} files at a time. ${rejectedOverflow} file${rejectedOverflow === 1 ? '' : 's'} were not added.`
        : null,
    )
  }

  const handleSelectClick = (): void => {
    fileInputRef.current?.click()
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDraggingOver(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDraggingOver(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDraggingOver(false)
    handleAddFiles(e.dataTransfer.files)
  }

  const handleSubmit = (): void => {
    setOverflowMessage(null)
    useUploadQueueStore.getState().submitUploads()
  }

  if (!isOpen) return null

  return (
    <Dialog
      open={isOpen}
      size="lg"
      onClose={handleClose}
      showCrossButton
      title="Upload files"
      actions={
        <div className="mr-2 flex items-center justify-end gap-2">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            raised
            disabled={!isUploadEnabled}
            onClick={handleSubmit}
          >
            {supportedCount > 0
              ? `Upload ${supportedCount} file${supportedCount === 1 ? '' : 's'}`
              : 'Upload files'}
          </Button>
        </div>
      }
    >
      <div className="-mt-4 flex flex-col gap-4 px-2">
        <Text
          appearance="BODY"
          className="text-text-secondary"
        >
          Files will be processed and ready for review.
        </Text>
        <div
          className={cn('h-[248px] gap-6', stagedFiles.length > 0 ? 'grid grid-cols-2' : 'flex')}
        >
          <div className="flex w-full min-w-0 flex-col gap-2">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'border-divider flex flex-1 flex-col items-center justify-center rounded-md border-2 border-dashed text-center transition-colors',
                isDraggingOver && 'border-primary-main bg-primary-main/5',
              )}
            >
              <div className="flex flex-col gap-6">
                <Text
                  appearance="BODY"
                  className="text-text-secondary"
                >
                  Choose PDF files or drag & drop here
                </Text>
                <div>
                  <Button
                    unelevated
                    tonal
                    onClick={handleSelectClick}
                  >
                    Select PDF
                  </Button>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleAddFiles(e.target.files)
                  // Reset so selecting the same file again fires onChange.
                  e.target.value = ''
                }}
              />
            </div>
            {overflowMessage && (
              <Text
                appearance="CAPTION"
                className="text-error-main"
              >
                {overflowMessage}
              </Text>
            )}
          </div>
          {stagedFiles.length > 0 && (
            <ScrollArea>
              <div className="flex min-w-0 flex-col gap-2">
                {stagedFiles.map((staged) => (
                  <UploadModalFileRow
                    key={staged.id}
                    staged={staged}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </Dialog>
  )
}
